import * as React from "react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export interface AnimatedTab {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  content: React.ReactNode;
}

interface AnimatedTabsProps {
  tabs: AnimatedTab[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}

export function AnimatedTabs({
  tabs,
  value,
  onValueChange,
  className,
}: AnimatedTabsProps) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const triggerRefs = React.useRef<Map<string, HTMLButtonElement>>(
    new Map()
  );
  const [indicator, setIndicator] = React.useState({
    left: 0,
    width: 0,
    ready: false,
  });

  const measure = React.useCallback((val: string) => {
    const list = listRef.current;
    const el = triggerRefs.current.get(val);
    if (!list || !el) return;
    setIndicator({
      left: el.offsetLeft,
      width: el.offsetWidth,
      ready: true,
    });
  }, []);

  React.useEffect(() => {
    measure(value);
  }, [value, measure]);

  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const ro = new ResizeObserver(() => measure(value));
    ro.observe(list);
    if (typeof document !== "undefined" && "fonts" in document) {
      document.fonts.ready.then(() => measure(value));
    }
    return () => ro.disconnect();
  }, [value, measure]);

  return (
    <Tabs
      value={value}
      onValueChange={onValueChange}
      className={cn("w-full", className)}
    >
      <TabsList
        ref={listRef}
        className="relative flex h-11 w-full rounded-lg bg-muted p-1"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute top-1 bottom-1 rounded-md bg-accent transition-all duration-300 ease-out"
          style={{
            left: indicator.left,
            width: indicator.width,
            opacity: indicator.ready ? 1 : 0,
          }}
        />
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              ref={(el: HTMLButtonElement | null) => {
                if (el) triggerRefs.current.set(tab.value, el);
                else triggerRefs.current.delete(tab.value);
              }}
              className="relative z-10 flex-1 gap-2 rounded-md bg-transparent text-foreground/60 shadow-none transition-colors duration-200 data-[state=active]:bg-transparent data-[state=active]:text-accent-foreground data-[state=active]:shadow-none"
            >
              {Icon && <Icon className="h-4 w-4" />}
              {tab.label}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent
          key={tab.value}
          value={tab.value}
          className="mt-6 data-[state=active]:animate-in data-[state=active]:fade-in-50 data-[state=active]:slide-in-from-bottom-1 data-[state=active]:duration-300"
        >
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}
